import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import isAudioFiles from '@medflex/utils/isAudioFiles';
import { CheckIcon } from '@heroicons/react/24/outline';
import getFullNameWithTitle from '@medflex/utils/getFullNameWithTitle';
import format from 'date-fns/format';
import locale from 'date-fns/locale/de';
import useClassNames from '@medflex/hooks/useClassNames';
import { IconExclamationSolid, IconTrash } from '../Icons';
import ChatMessageBubble from '../ChatMessageBubble/ChatMessageBubble';
import PatientMessage from './Patient';
import FileMessage from './File';
import Player from '../VoiceMessage/Player';
import Tooltip from '../Tooltip';

const ChatMessage = ({
  message,
  messageTypeLabels,
  MessageActionMenu,
  deletedMessageText,
  fileMessageClickHandler,
  audioSourceResolver,
  messageSeenText,
  PatientMessageAction,
  isUserPatient,
}) => {
  const [isHover, setIsHover] = useState(false);
  const isImportant = !!message.markedImportantAt;
  const isChecked = !!message.checkedAt;
  let toggleActionMenu;
  const [clickStarted, setClickStarted] = useState(false);
  const [timerId, setTimerId] = useState();
  const messageRef = useRef();
  const { t } = useTranslation('components');

  const hasFiles = message.file || (message.files && message.files.length > 0);

  const isAudioMessage = useMemo(() => {
    if (!message || !message.files) {
      return false;
    }
    return isAudioFiles(message.files);
  }, [message]);

  useEffect(() => {
    if (clickStarted) {
      setTimerId(
        setTimeout(() => {
          toggleActionMenu();
          setClickStarted(false);
        }, 500),
      );
    } else {
      clearTimeout(timerId);
      setTimerId(null);
    }

    return () => {
      clearTimeout(timerId);
      setTimerId(null);
    };
  }, [clickStarted]);

  const chatMessageCn = useClassNames(() => [
    'relative',
    'flex',
    'gap-2',
    (message.isSender || message.isInSenderPractice) && 'flex-row-reverse',
  ]);

  const chatMessageBubbleWrapperCn = useClassNames(
    () => ['inline-flex', ...(isAudioMessage ? ['w-full', 'max-w-lg'] : [])],
    [isAudioMessage],
  );

  if (!message.deletedAt) {
    return (
      <div
        className={chatMessageCn}
        data-cy="chat-message"
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onTouchStart={() => setClickStarted(true)}
        onTouchMove={() => setClickStarted(false)}
        onTouchEnd={() => setClickStarted(false)}
        ref={messageRef}
      >
        <div className="flex flex-col pt-1">
          <IconExclamationSolid
            className={`text-red-600 mb-2 ${!isImportant && 'invisible'}`}
            data-cy="icon-exclamation"
            size="6"
          />

          {isChecked && !isUserPatient && (
            <Tooltip
              title={`${getFullNameWithTitle(message.checkedBy)} ${format(
                message.checkedAt,
                'DD.MM.YY, HH:mm',
                { locale },
              )}`}
            >
              <div className="ring-1 rounded bg-green-200 ring-green-400">
                <CheckIcon className="w-6 h-6 text-green-800" />
              </div>
            </Tooltip>
          )}
        </div>

        <div className={chatMessageBubbleWrapperCn}>
          <ChatMessageBubble
            seenBy={message.seenBy}
            seen={message.seen}
            messageSeenText={messageSeenText}
            className={[
              !message.isSender &&
                !(message.type && message.type === 'multicast') &&
                'bg-white shadow-md',
              message.isSender && !(message.type && message.type === 'multicast') && 'bg-blue-100',
              message.type &&
                message.type === 'multicast' &&
                'border-l-8 border-slate-300 rounded-l-none my-4',
            ]}
            isAudioMessage={isAudioMessage}
          >
            {message.patient && (
              <div>
                <PatientMessage message={message} PatientMessageAction={PatientMessageAction} />
              </div>
            )}
            {isAudioMessage && (
              <Player
                src={audioSourceResolver}
                initialDuration={message.files[0].duration}
                caption={t('ChatMessage.recording')}
                defer
              />
            )}
            {hasFiles && !isAudioMessage && (
              <div>
                <FileMessage
                  className="mb-2"
                  message={message}
                  clickHandler={fileMessageClickHandler}
                />
                {messageTypeLabels && messageTypeLabels[message.type] && (
                  <p className="text-xs text-slate-500 text-right">
                    {messageTypeLabels[message.type]}
                  </p>
                )}
              </div>
            )}
            {message.text && !hasFiles && (
              <div>
                <p className="mb-2">{message.text}</p>
                {messageTypeLabels && messageTypeLabels[message.type] && (
                  <p className="text-xs text-slate-500 text-right">
                    {messageTypeLabels[message.type]}
                  </p>
                )}
              </div>
            )}
          </ChatMessageBubble>
        </div>
        {MessageActionMenu && (
          <div className="inline-flex align-top" data-cy="chat-message-action-menu">
            {React.cloneElement(
              MessageActionMenu(),
              {
                messageId: message.id,
                message,
                hidden: !isHover,
                onClickOutside: e => {
                  if (!(messageRef.current && messageRef.current.contains(e.target))) {
                    setIsHover(isHover);
                  }
                },
                messageRef,
              },
              // eslint-disable-next-line no-return-assign
              ({ toggle }) => (toggleActionMenu = toggle),
            )}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className={chatMessageCn}>
      <div className="w-6" />
      <ChatMessageBubble className={['bg-slate-200']}>
        <div className="flex items-center">
          <IconTrash className="text-slate-400" size="5" />
          <span className="text-slate-500 p-1">{deletedMessageText}</span>
        </div>
      </ChatMessageBubble>
    </div>
  );
};

export default ChatMessage;
